import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Grid2x2 } from 'lucide-react'

import { SearchSelect } from '@/components/SearchSelect'
import { LayoutsApi } from '@/services/sites/layoutsApi'

export interface LayoutSelectProps {
  /** Layout id, or '' for no selection. */
  value: string
  onChange: (layoutId: string) => void
  /** Label for the empty choice, offered first to clear the selection. */
  anyLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Searchable layout picker. With `anyLabel` it serves as a list filter, where
 * '' means "not filtered".
 */
export function LayoutSelect({ anyLabel, ...props }: LayoutSelectProps) {
  const { t } = useTranslation()

  // Shares the cached layout list the creative table and editors read.
  const { data: layouts = [], isLoading } = useQuery({
    queryKey: ['layouts'],
    queryFn: () => LayoutsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })
  const options = useMemo(() => layouts.map((l) => ({ value: l.id, label: l.name })), [layouts])

  return (
    <SearchSelect
      {...props}
      options={options}
      placeholder={anyLabel ?? t('creatives.selectLayout')}
      clearable={!!anyLabel}
      icon={Grid2x2}
      loading={isLoading}
      searchPlaceholder={t('layouts.searchLayouts')}
      emptyText={t('layouts.noLayoutsFound')}
    />
  )
}
